import { supabase } from '../supabase';
import { SettlementDetailRevenue } from '../utils/types';

export async function getRevenuesBySettlementDetailId(
  detailId: number
): Promise<SettlementDetailRevenue[] | null> {
  try {
    const { data, error } = await supabase
      .from('settlement_detail_revenues')
      .select('*')
      .eq('settlement_detail_id', detailId);

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting revenues by settlement detail id:', error);
    return null;
  }
}

export async function getRevenuesBySettlementId(
  settlementId: number
): Promise<SettlementDetailRevenue[] | null> {
  try {
    const { data, error } = await supabase
      .from('settlement_detail_revenues')
      .select('*, settlements_details!inner(settlement_id)')
      .eq('settlements_details.settlement_id', settlementId);

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting revenues by settlement id:', error);
    return null;
  }
}

export async function createSettlementDetailRevenue(
  revenue: Omit<SettlementDetailRevenue, 'id' | 'created_at'>
): Promise<SettlementDetailRevenue | null> {
  try {
    const { data, error } = await supabase
      .from('settlement_detail_revenues')
      .insert([revenue])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating settlement detail revenue:', error);
    return null;
  }
}

export async function updateSettlementDetailRevenue(
  id: number,
  revenue: Partial<SettlementDetailRevenue>
): Promise<SettlementDetailRevenue | null> {
  try {
    const { data, error } = await supabase
      .from('settlement_detail_revenues')
      .update(revenue)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating settlement detail revenue:', error);
    return null;
  }
}

export async function deleteSettlementDetailRevenue(
  id: number
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('settlement_detail_revenues')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting settlement detail revenue:', error);
    return false;
  }
}

export async function deleteRevenuesBySettlementAndType(
  settlementId: number,
  revenueType: string
): Promise<boolean> {
  try {
    const { data: details, error: detailsError } = await supabase
      .from('settlements_details')
      .select('id')
      .eq('settlement_id', settlementId);

    if (detailsError) throw detailsError;
    if (!details || details.length === 0) return true;

    const detailIds = details.map((d) => d.id);
    const { error } = await supabase
      .from('settlement_detail_revenues')
      .delete()
      .in('settlement_detail_id', detailIds)
      .eq('revenue_type', revenueType);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting revenues by settlement and type:', error);
    return false;
  }
}

export async function bulkCreateSettlementDetailRevenues(
  revenues: Omit<SettlementDetailRevenue, 'id' | 'created_at'>[]
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('settlement_detail_revenues')
      .insert(revenues);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error bulk creating settlement detail revenues:', error);
    return false;
  }
}
